export type DataPoint = { date: string; value: number };

export type SeriesBundle = {
    data: DataPoint[];
    marks: number[];
    thresholds: { min?: number; max?: number; showOnChart: boolean };
};

export type ScaleKey = "hours" | "days" | "weeks" | "months";

export type PeriodKey =
    | "today"
    | "yesterday"
    | "week"
    | "month"
    | "year"
    | "custom_1_2_days"
    | "custom_3_7_days"
    | "custom_8_46_days"
    | "custom_47_plus";

export type PeriodScaleMap = {
    [k in ScaleKey]?: SeriesBundle;
};

export type MetricBundle = {
    [k in PeriodKey]?: PeriodScaleMap;
};

export type HiveChartsMocks = {
    happiness: MetricBundle;
    temperature: MetricBundle;
    humidity: MetricBundle;
    co2: MetricBundle;
    weight: MetricBundle;
    strength: MetricBundle;
    brood: MetricBundle;
    sound: MetricBundle;
};

function iso(d: Date): string {
    return new Date(d.getTime() - d.getMilliseconds()).toISOString();
}

function clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, v));
}

function rnd(seed: number): () => number {
    let s = seed >>> 0;
    return () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return (s & 0xffff) / 0xffff;
    };
}

function seriesByStep(opts: {
    start: Date;
    points: number;
    stepMinutes: number;
    base: number;
    spread: number;
    min: number;
    max: number;
    seed: number;
    decimals?: number;
}): DataPoint[] {
    const { start, points, stepMinutes, base, spread, min, max, seed, decimals = 2 } = opts;
    const rand = rnd(seed);
    const arr: DataPoint[] = [];
    for (let i = 0; i < points; i++) {
        const t = new Date(start.getTime() + i * stepMinutes * 60 * 1000);
        const drift = Math.sin(i / 3) * spread * 0.4 + Math.cos(i / 5) * spread * 0.3;
        const noise = (rand() - 0.5) * spread;
        const val = clamp(base + drift + noise, min, max);
        arr.push({ date: iso(t), value: Number(val.toFixed(decimals)) });
    }
    return arr;
}

function seriesDaily(opts: {
    start: Date;
    days: number;
    base: number;
    spread: number;
    min: number;
    max: number;
    seed: number;
    decimals?: number;
}): DataPoint[] {
    return seriesByStep({
        start: opts.start,
        points: opts.days,
        stepMinutes: 60 * 24,
        base: opts.base,
        spread: opts.spread,
        min: opts.min,
        max: opts.max,
        seed: opts.seed,
        decimals: opts.decimals,
    });
}

function seriesWeekly(opts: {
    start: Date;
    weeks: number;
    base: number;
    spread: number;
    min: number;
    max: number;
    seed: number;
    decimals?: number;
}): DataPoint[] {
    return seriesByStep({
        start: opts.start,
        points: opts.weeks,
        stepMinutes: 60 * 24 * 7,
        base: opts.base,
        spread: opts.spread,
        min: opts.min,
        max: opts.max,
        seed: opts.seed,
        decimals: opts.decimals,
    });
}

function seriesMonthly(opts: {
    start: Date;
    months: number;
    base: number;
    spread: number;
    min: number;
    max: number;
    seed: number;
    decimals?: number;
}): DataPoint[] {
    const { start, months, base, spread, min, max, seed, decimals = 2 } = opts;
    const rand = rnd(seed);
    const arr: DataPoint[] = [];
    for (let i = 0; i < months; i++) {
        const t = new Date(start);
        t.setMonth(t.getMonth() + i);
        const drift = Math.sin(i / 2) * spread * 0.5 + Math.cos(i / 3) * spread * 0.2;
        const noise = (rand() - 0.5) * spread;
        const val = clamp(base + drift + noise, min, max);
        arr.push({ date: iso(t), value: Number(val.toFixed(decimals)) });
    }
    return arr;
}

function bundle(
    data: DataPoint[],
    marks: number[],
    thresholds: { min?: number; max?: number; showOnChart: boolean }
): SeriesBundle {
    return { data, marks, thresholds };
}

const now = new Date();
const nowRounded = new Date(now);
nowRounded.setMinutes(0, 0, 0);

const todayStart = new Date(now);
todayStart.setHours(0, 0, 0, 0);
const yesterdayStart = new Date(todayStart);
yesterdayStart.setDate(yesterdayStart.getDate() - 1);
const weekStart = new Date(todayStart);
weekStart.setDate(weekStart.getDate() - 6);
const monthStart = new Date(todayStart);
monthStart.setDate(monthStart.getDate() - 29);
const yearStart = new Date(todayStart);
yearStart.setMonth(yearStart.getMonth() - 11);

function todayPointsFrom(start: Date, stepMinutes: number): number {
    const diffMs = Math.max(0, nowRounded.getTime() - start.getTime());
    const stepMs = stepMinutes * 60 * 1000;
    const steps = Math.floor(diffMs / stepMs) + 1;
    return clamp(steps, 1, Math.floor((24 * 60) / stepMinutes));
}

function seriesTodayFlat(opts: {
    start: Date;
    base: number;
    noise: number;
    min: number;
    max: number;
    seed: number;
    stepMinutes?: number;
}): DataPoint[] {
    const stepMinutes = opts.stepMinutes ?? 60;
    const points = todayPointsFrom(opts.start, stepMinutes);
    const rand = rnd(opts.seed);
    const arr: DataPoint[] = [];
    for (let i = 0; i < points; i++) {
        const t = new Date(opts.start.getTime() + i * stepMinutes * 60 * 1000);
        const drift = Math.sin(i / 8) * opts.noise * 0.3;
        const noise = (rand() - 0.5) * opts.noise;
        const v = clamp(opts.base + drift + noise, opts.min, opts.max);
        arr.push({ date: iso(t), value: Number(v.toFixed(2)) });
    }
    return arr;
}

function seriesTodayTemp(opts: {
    start: Date;
    base: number;
    noise: number;
    eveningStartHour: number;
    eveningDropPerHour: number;
    min: number;
    max: number;
    seed: number;
}): DataPoint[] {
    const stepMinutes = 60;
    const points = todayPointsFrom(opts.start, stepMinutes);
    const rand = rnd(opts.seed);
    const arr: DataPoint[] = [];
    for (let i = 0; i < points; i++) {
        const t = new Date(opts.start.getTime() + i * stepMinutes * 60 * 1000);
        const hour = t.getHours();
        const dropSteps = Math.max(0, hour - opts.eveningStartHour);
        const trend = -dropSteps * opts.eveningDropPerHour;
        const noise = (rand() - 0.5) * opts.noise;
        const v = clamp(opts.base + trend + noise, opts.min, opts.max);
        arr.push({ date: iso(t), value: Number(v.toFixed(2)) });
    }
    return arr;
}

function seriesTodayWeightMixed(opts: {
    start: Date;
    base: number;
    gainPerHour: number;
    noise: number;
    dipChance: number;
    dipMax: number;
    min: number;
    max: number;
    seed: number;
}): DataPoint[] {
    const stepMinutes = 60;
    const points = todayPointsFrom(opts.start, stepMinutes);
    const rand = rnd(opts.seed);
    const arr: DataPoint[] = [];
    let prev = opts.base;
    for (let i = 0; i < points; i++) {
        const t = new Date(opts.start.getTime() + i * stepMinutes * 60 * 1000);
        const planned = opts.base + i * opts.gainPerHour;
        let v = planned + (rand() - 0.5) * opts.noise;
        if (rand() < opts.dipChance) v -= rand() * opts.dipMax;
        v = Math.max(v, prev - 0.06);
        v = clamp(v, opts.min, opts.max);
        prev = v;
        arr.push({ date: iso(t), value: Number(v.toFixed(2)) });
    }
    return arr;
}

/**
 * HAPPINESS
 */
const happinessTodayHours = seriesByStep({
    start: todayStart,
    points: todayPointsFrom(todayStart, 60),
    stepMinutes: 60,
    base: 3.5,
    spread: 1.2,
    min: 0,
    max: 5,
    seed: 11,
    decimals: 1,
});
const happinessYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 3.2,
    spread: 1.4,
    min: 0,
    max: 5,
    seed: 12,
    decimals: 1,
});
const happinessWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 3.6,
    spread: 1.1,
    min: 0,
    max: 5,
    seed: 13,
    decimals: 1,
});
const happinessWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 3.6,
    spread: 0.8,
    min: 0,
    max: 5,
    seed: 14,
    decimals: 1,
});
const happinessMonthHours = seriesByStep({
    start: monthStart,
    points: 30 * 6,
    stepMinutes: 240,
    base: 3.7,
    spread: 1.2,
    min: 0,
    max: 5,
    seed: 15,
    decimals: 1,
});
const happinessMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 3.7,
    spread: 0.9,
    min: 0,
    max: 5,
    seed: 16,
    decimals: 1,
});
const happinessMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 3.8,
    spread: 0.7,
    min: 0,
    max: 5,
    seed: 17,
    decimals: 1,
});
const happinessYearDays = seriesDaily({
    start: yearStart,
    days: (365 / 12) * 12,
    base: 3.9,
    spread: 0.8,
    min: 0,
    max: 5,
    seed: 18,
    decimals: 1,
});
const happinessYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 3.9,
    spread: 0.7,
    min: 0,
    max: 5,
    seed: 19,
    decimals: 1,
});
const happinessYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 4.0,
    spread: 0.6,
    min: 0,
    max: 5,
    seed: 20,
    decimals: 1,
});

/**
 * TEMPERATURE
 */
const tempTodayHours = seriesTodayTemp({
    start: todayStart,
    base: 32.5,
    noise: 0.8,
    eveningStartHour: 15,
    eveningDropPerHour: 0.08,
    min: 25,
    max: 38,
    seed: 21,
});
const tempYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 31,
    spread: 3,
    min: 24,
    max: 38,
    seed: 22,
});
const tempWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 32,
    spread: 3,
    min: 24,
    max: 39,
    seed: 23,
});
const tempWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 32,
    spread: 2.5,
    min: 24,
    max: 39,
    seed: 24,
});
const tempMonthHours = seriesByStep({
    start: monthStart,
    points: 30 * 6,
    stepMinutes: 240,
    base: 32,
    spread: 3.5,
    min: 23,
    max: 40,
    seed: 25,
});
const tempMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 32,
    spread: 3,
    min: 23,
    max: 40,
    seed: 26,
});
const tempMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 32,
    spread: 2.5,
    min: 23,
    max: 40,
    seed: 27,
});
const tempYearDays = seriesDaily({
    start: yearStart,
    days: (365 / 12) * 12,
    base: 32,
    spread: 3,
    min: 22,
    max: 41,
    seed: 28,
});
const tempYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 32,
    spread: 2.5,
    min: 22,
    max: 41,
    seed: 29,
});
const tempYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 32,
    spread: 2,
    min: 22,
    max: 41,
    seed: 30,
});

/**
 * HUMIDITY
 */
const humTodayHours = seriesTodayFlat({
    start: todayStart,
    base: 62,
    noise: 2,
    min: 30,
    max: 95,
    seed: 31,
});
const humYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 60,
    spread: 12,
    min: 30,
    max: 95,
    seed: 32,
});
const humWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 61,
    spread: 12,
    min: 30,
    max: 95,
    seed: 33,
});
const humWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 61,
    spread: 10,
    min: 30,
    max: 95,
    seed: 34,
});
const humMonthHours = seriesByStep({
    start: monthStart,
    points: 30 * 6,
    stepMinutes: 240,
    base: 60,
    spread: 12,
    min: 25,
    max: 98,
    seed: 35,
});
const humMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 60,
    spread: 11,
    min: 25,
    max: 98,
    seed: 36,
});
const humMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 60,
    spread: 10,
    min: 25,
    max: 98,
    seed: 37,
});
const humYearDays = seriesDaily({
    start: yearStart,
    days: (365 / 12) * 12,
    base: 58,
    spread: 12,
    min: 20,
    max: 98,
    seed: 38,
});
const humYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 58,
    spread: 11,
    min: 20,
    max: 98,
    seed: 39,
});
const humYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 58,
    spread: 10,
    min: 20,
    max: 98,
    seed: 40,
});

/**
 * CO2
 */
const co2TodayHours = seriesTodayFlat({
    start: todayStart,
    base: 820,
    noise: 40,
    min: 400,
    max: 2500,
    seed: 41,
});
const co2YesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 850,
    spread: 240,
    min: 400,
    max: 2500,
    seed: 42,
});
const co2WeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 880,
    spread: 260,
    min: 400,
    max: 3000,
    seed: 43,
});
const co2WeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 880,
    spread: 250,
    min: 400,
    max: 3000,
    seed: 44,
});
const co2MonthHours = seriesByStep({
    start: monthStart,
    points: 30 * 6,
    stepMinutes: 240,
    base: 900,
    spread: 300,
    min: 400,
    max: 3200,
    seed: 45,
});
const co2MonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 900,
    spread: 280,
    min: 400,
    max: 3200,
    seed: 46,
});
const co2MonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 900,
    spread: 260,
    min: 400,
    max: 3200,
    seed: 47,
});
const co2YearDays = seriesDaily({
    start: yearStart,
    days: (365 / 12) * 12,
    base: 920,
    spread: 300,
    min: 350,
    max: 3500,
    seed: 48,
});
const co2YearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 920,
    spread: 280,
    min: 350,
    max: 3500,
    seed: 49,
});
const co2YearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 920,
    spread: 260,
    min: 350,
    max: 3500,
    seed: 50,
});

/**
 * WEIGHT
 */
const weightTodayHours = seriesTodayWeightMixed({
    start: todayStart,
    base: 74.0,
    gainPerHour: 0.045,
    noise: 0.12,
    dipChance: 0.18,
    dipMax: 0.08,
    min: 60,
    max: 100,
    seed: 61,
});
const weightYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 73.5,
    spread: 2.0,
    min: 60,
    max: 100,
    seed: 62,
});
const weightWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 74.0,
    spread: 2.2,
    min: 60,
    max: 105,
    seed: 63,
});
const weightWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 74.0,
    spread: 1.6,
    min: 60,
    max: 105,
    seed: 64,
});
const weightMonthHours = seriesByStep({
    start: monthStart,
    points: 30 * 6,
    stepMinutes: 240,
    base: 73.8,
    spread: 2.4,
    min: 55,
    max: 110,
    seed: 65,
});
const weightMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 73.8,
    spread: 2.0,
    min: 55,
    max: 110,
    seed: 66,
});
const weightMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 73.8,
    spread: 1.6,
    min: 55,
    max: 110,
    seed: 67,
});
const weightYearDays = seriesDaily({
    start: yearStart,
    days: (365 / 12) * 12,
    base: 72.0,
    spread: 3.0,
    min: 50,
    max: 120,
    seed: 68,
});
const weightYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 72.0,
    spread: 2.6,
    min: 50,
    max: 120,
    seed: 69,
});
const weightYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 72.0,
    spread: 2.2,
    min: 50,
    max: 120,
    seed: 70,
});

/**
 * STRENGTH
 */
const strengthTodayHours = seriesByStep({
    start: todayStart,
    points: todayPointsFrom(todayStart, 60),
    stepMinutes: 60,
    base: 8.0,
    spread: 1.2,
    min: 4,
    max: 10,
    seed: 71,
    decimals: 0,
});
const strengthYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 7.8,
    spread: 1.4,
    min: 4,
    max: 10,
    seed: 72,
    decimals: 0,
});
const strengthWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 8.2,
    spread: 1.2,
    min: 4,
    max: 10,
    seed: 73,
    decimals: 0,
});
const strengthWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 8.2,
    spread: 1.0,
    min: 4,
    max: 10,
    seed: 74,
    decimals: 0,
});
const strengthMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 8.0,
    spread: 1.2,
    min: 3,
    max: 10,
    seed: 75,
    decimals: 0,
});
const strengthMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 8.0,
    spread: 1.0,
    min: 3,
    max: 10,
    seed: 76,
    decimals: 0,
});
const strengthYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 7.5,
    spread: 1.2,
    min: 2,
    max: 10,
    seed: 77,
    decimals: 0,
});
const strengthYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 7.5,
    spread: 1.0,
    min: 2,
    max: 10,
    seed: 78,
    decimals: 0,
});

/**
 * BROOD
 */
const broodTodayHours = seriesByStep({
    start: todayStart,
    points: todayPointsFrom(todayStart, 60),
    stepMinutes: 60,
    base: 4.2,
    spread: 0.8,
    min: 0,
    max: 9,
    seed: 81,
    decimals: 0,
});
const broodYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 4.0,
    spread: 0.9,
    min: 0,
    max: 9,
    seed: 82,
    decimals: 0,
});
const broodWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 4.1,
    spread: 0.9,
    min: 0,
    max: 9,
    seed: 83,
    decimals: 0,
});
const broodWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 4.1,
    spread: 0.8,
    min: 0,
    max: 9,
    seed: 84,
    decimals: 0,
});
const broodMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 4.0,
    spread: 1.0,
    min: 0,
    max: 9,
    seed: 85,
    decimals: 0,
});
const broodMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 4.0,
    spread: 0.9,
    min: 0,
    max: 9,
    seed: 86,
    decimals: 0,
});
const broodYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 3.8,
    spread: 1.1,
    min: 0,
    max: 9,
    seed: 87,
    decimals: 0,
});
const broodYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 3.8,
    spread: 1.0,
    min: 0,
    max: 9,
    seed: 88,
    decimals: 0,
});

/**
 * SOUND
 */
const soundTodayHours = seriesByStep({
    start: todayStart,
    points: todayPointsFrom(todayStart, 60),
    stepMinutes: 60,
    base: 45,
    spread: 8,
    min: 20,
    max: 90,
    seed: 91,
});
const soundYesterdayHours = seriesByStep({
    start: yesterdayStart,
    points: 24,
    stepMinutes: 60,
    base: 44,
    spread: 9,
    min: 20,
    max: 90,
    seed: 92,
});
const soundWeekHours = seriesByStep({
    start: weekStart,
    points: 7 * 24,
    stepMinutes: 60,
    base: 46,
    spread: 10,
    min: 20,
    max: 95,
    seed: 93,
});
const soundWeekDays = seriesDaily({
    start: weekStart,
    days: 7,
    base: 46,
    spread: 9,
    min: 20,
    max: 95,
    seed: 94,
});
const soundMonthDays = seriesDaily({
    start: monthStart,
    days: 30,
    base: 45,
    spread: 10,
    min: 20,
    max: 95,
    seed: 95,
});
const soundMonthWeeks = seriesWeekly({
    start: monthStart,
    weeks: 5,
    base: 45,
    spread: 9,
    min: 20,
    max: 95,
    seed: 96,
});
const soundYearWeeks = seriesWeekly({
    start: yearStart,
    weeks: 52,
    base: 44,
    spread: 10,
    min: 20,
    max: 95,
    seed: 97,
});
const soundYearMonths = seriesMonthly({
    start: yearStart,
    months: 12,
    base: 44,
    spread: 9,
    min: 20,
    max: 95,
    seed: 98,
});

export const hiveChartsMocks: HiveChartsMocks = {
    happiness: {
        today: {
            hours: bundle(happinessTodayHours, [4], { min: 1.5, max: 4.8, showOnChart: true }),
        },
        yesterday: {
            hours: bundle(happinessYesterdayHours, [3.5], { min: 1.2, max: 4.6, showOnChart: true }),
        },
        week: {
            hours: bundle(happinessWeekHours, [4.2], { min: 1.5, max: 4.9, showOnChart: true }),
            days: bundle(happinessWeekDays, [4.2], { min: 1.5, max: 4.9, showOnChart: true }),
        },
        month: {
            hours: bundle(happinessMonthHours, [4.0], { min: 1.5, max: 4.9, showOnChart: false }),
            days: bundle(happinessMonthDays, [4.0], { min: 1.5, max: 4.9, showOnChart: true }),
            weeks: bundle(happinessMonthWeeks, [4.0], { min: 1.5, max: 4.9, showOnChart: true }),
        },
        year: {
            days: bundle(happinessYearDays, [3.8], { min: 1.0, max: 5.0, showOnChart: false }),
            weeks: bundle(happinessYearWeeks, [3.8], { min: 1.0, max: 5.0, showOnChart: true }),
            months: bundle(happinessYearMonths, [3.8], { min: 1.0, max: 5.0, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(happinessTodayHours, [4.3], { min: 1.5, max: 4.8, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(happinessWeekHours, [4.1], { min: 1.5, max: 4.9, showOnChart: true }),
            days: bundle(happinessWeekDays, [4.1], { min: 1.5, max: 4.9, showOnChart: true }),
        },
        custom_8_46_days: {
            hours: bundle(happinessMonthHours, [3.9], { min: 1.5, max: 4.9, showOnChart: false }),
            days: bundle(happinessMonthDays, [3.9], { min: 1.5, max: 4.9, showOnChart: true }),
            weeks: bundle(happinessMonthWeeks, [3.9], { min: 1.5, max: 4.9, showOnChart: true }),
        },
        custom_47_plus: {
            days: bundle(happinessYearDays, [3.7], { min: 1.0, max: 5.0, showOnChart: false }),
            weeks: bundle(happinessYearWeeks, [3.7], { min: 1.0, max: 5.0, showOnChart: true }),
            months: bundle(happinessYearMonths, [3.7], { min: 1.0, max: 5.0, showOnChart: true }),
        },
    },
    temperature: {
        today: { hours: bundle(tempTodayHours, [35], { min: 32, max: 36, showOnChart: true }) },
        yesterday: { hours: bundle(tempYesterdayHours, [35], { min: 31, max: 36, showOnChart: true }) },
        week: {
            hours: bundle(tempWeekHours, [35], { min: 31, max: 37, showOnChart: true }),
            days: bundle(tempWeekDays, [35], { min: 31, max: 37, showOnChart: true }),
        },
        month: {
            hours: bundle(tempMonthHours, [35], { min: 30, max: 37, showOnChart: false }),
            days: bundle(tempMonthDays, [35], { min: 30, max: 37, showOnChart: true }),
            weeks: bundle(tempMonthWeeks, [35], { min: 30, max: 37, showOnChart: true }),
        },
        year: {
            days: bundle(tempYearDays, [35], { min: 29, max: 38, showOnChart: false }),
            weeks: bundle(tempYearWeeks, [35], { min: 29, max: 38, showOnChart: true }),
            months: bundle(tempYearMonths, [35], { min: 29, max: 38, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(tempTodayHours, [35], { min: 32, max: 36, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(tempWeekHours, [35], { min: 31, max: 37, showOnChart: true }),
            days: bundle(tempWeekDays, [35], { min: 31, max: 37, showOnChart: true }),
        },
        custom_8_46_days: {
            hours: bundle(tempMonthHours, [35], { min: 30, max: 37, showOnChart: false }),
            days: bundle(tempMonthDays, [35], { min: 30, max: 37, showOnChart: true }),
            weeks: bundle(tempMonthWeeks, [35], { min: 30, max: 37, showOnChart: true }),
        },
        custom_47_plus: {
            days: bundle(tempYearDays, [35], { min: 29, max: 38, showOnChart: false }),
            weeks: bundle(tempYearWeeks, [35], { min: 29, max: 38, showOnChart: true }),
            months: bundle(tempYearMonths, [35], { min: 29, max: 38, showOnChart: true }),
        },
    },
    humidity: {
        today: { hours: bundle(humTodayHours, [70], { min: 45, max: 85, showOnChart: true }) },
        yesterday: { hours: bundle(humYesterdayHours, [70], { min: 45, max: 85, showOnChart: true }) },
        week: {
            hours: bundle(humWeekHours, [70], { min: 45, max: 85, showOnChart: true }),
            days: bundle(humWeekDays, [70], { min: 45, max: 85, showOnChart: true }),
        },
        month: {
            hours: bundle(humMonthHours, [70], { min: 40, max: 90, showOnChart: false }),
            days: bundle(humMonthDays, [70], { min: 40, max: 90, showOnChart: true }),
            weeks: bundle(humMonthWeeks, [70], { min: 40, max: 90, showOnChart: true }),
        },
        year: {
            days: bundle(humYearDays, [68], { min: 35, max: 90, showOnChart: false }),
            weeks: bundle(humYearWeeks, [68], { min: 35, max: 90, showOnChart: true }),
            months: bundle(humYearMonths, [68], { min: 35, max: 90, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(humTodayHours, [70], { min: 45, max: 85, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(humWeekHours, [70], { min: 45, max: 85, showOnChart: true }),
            days: bundle(humWeekDays, [70], { min: 45, max: 85, showOnChart: true }),
        },
        custom_8_46_days: {
            hours: bundle(humMonthHours, [68], { min: 40, max: 90, showOnChart: false }),
            days: bundle(humMonthDays, [68], { min: 40, max: 90, showOnChart: true }),
            weeks: bundle(humMonthWeeks, [68], { min: 40, max: 90, showOnChart: true }),
        },
        custom_47_plus: {
            days: bundle(humYearDays, [66], { min: 35, max: 90, showOnChart: false }),
            weeks: bundle(humYearWeeks, [66], { min: 35, max: 90, showOnChart: true }),
            months: bundle(humYearMonths, [66], { min: 35, max: 90, showOnChart: true }),
        },
    },
    co2: {
        today: { hours: bundle(co2TodayHours, [1200], { min: 700, max: 1600, showOnChart: true }) },
        yesterday: { hours: bundle(co2YesterdayHours, [1200], { min: 700, max: 1600, showOnChart: true }) },
        week: {
            hours: bundle(co2WeekHours, [1200], { min: 700, max: 1800, showOnChart: true }),
            days: bundle(co2WeekDays, [1200], { min: 700, max: 1800, showOnChart: true }),
        },
        month: {
            hours: bundle(co2MonthHours, [1200], { min: 700, max: 2000, showOnChart: false }),
            days: bundle(co2MonthDays, [1200], { min: 700, max: 2000, showOnChart: true }),
            weeks: bundle(co2MonthWeeks, [1200], { min: 700, max: 2000, showOnChart: true }),
        },
        year: {
            days: bundle(co2YearDays, [1100], { min: 650, max: 2200, showOnChart: false }),
            weeks: bundle(co2YearWeeks, [1100], { min: 650, max: 2200, showOnChart: true }),
            months: bundle(co2YearMonths, [1100], { min: 650, max: 2200, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(co2TodayHours, [1200], { min: 700, max: 1600, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(co2WeekHours, [1200], { min: 700, max: 1800, showOnChart: true }),
            days: bundle(co2WeekDays, [1200], { min: 700, max: 1800, showOnChart: true }),
        },
        custom_8_46_days: {
            hours: bundle(co2MonthHours, [1150], { min: 700, max: 2000, showOnChart: false }),
            days: bundle(co2MonthDays, [1150], { min: 700, max: 2000, showOnChart: true }),
            weeks: bundle(co2MonthWeeks, [1150], { min: 700, max: 2000, showOnChart: true }),
        },
        custom_47_plus: {
            days: bundle(co2YearDays, [1100], { min: 650, max: 2200, showOnChart: false }),
            weeks: bundle(co2YearWeeks, [1100], { min: 650, max: 2200, showOnChart: true }),
            months: bundle(co2YearMonths, [1100], { min: 650, max: 2200, showOnChart: true }),
        },
    },
    weight: {
        today: { hours: bundle(weightTodayHours, [75], { min: 65, max: 95, showOnChart: true }) },
        yesterday: { hours: bundle(weightYesterdayHours, [75], { min: 65, max: 95, showOnChart: true }) },
        week: {
            hours: bundle(weightWeekHours, [75], { min: 65, max: 100, showOnChart: true }),
            days: bundle(weightWeekDays, [75], { min: 65, max: 100, showOnChart: true }),
        },
        month: {
            hours: bundle(weightMonthHours, [74], { min: 60, max: 105, showOnChart: false }),
            days: bundle(weightMonthDays, [74], { min: 60, max: 105, showOnChart: true }),
            weeks: bundle(weightMonthWeeks, [74], { min: 60, max: 105, showOnChart: true }),
        },
        year: {
            days: bundle(weightYearDays, [73], { min: 55, max: 115, showOnChart: false }),
            weeks: bundle(weightYearWeeks, [73], { min: 55, max: 115, showOnChart: true }),
            months: bundle(weightYearMonths, [73], { min: 55, max: 115, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(weightTodayHours, [75], { min: 65, max: 95, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(weightWeekHours, [75], { min: 65, max: 100, showOnChart: true }),
            days: bundle(weightWeekDays, [75], { min: 65, max: 100, showOnChart: true }),
        },
        custom_8_46_days: {
            hours: bundle(weightMonthHours, [74], { min: 60, max: 105, showOnChart: false }),
            days: bundle(weightMonthDays, [74], { min: 60, max: 105, showOnChart: true }),
            weeks: bundle(weightMonthWeeks, [74], { min: 60, max: 105, showOnChart: true }),
        },
        custom_47_plus: {
            days: bundle(weightYearDays, [73], { min: 55, max: 115, showOnChart: false }),
            weeks: bundle(weightYearWeeks, [73], { min: 55, max: 115, showOnChart: true }),
            months: bundle(weightYearMonths, [73], { min: 55, max: 115, showOnChart: true }),
        },
    },
    strength: {
        today: { hours: bundle(strengthTodayHours, [9], { min: 5, max: 10, showOnChart: true }) },
        yesterday: { hours: bundle(strengthYesterdayHours, [9], { min: 5, max: 10, showOnChart: true }) },
        week: {
            hours: bundle(strengthWeekHours, [9], { min: 5, max: 10, showOnChart: true }),
            days: bundle(strengthWeekDays, [9], { min: 5, max: 10, showOnChart: true }),
        },
        month: {
            days: bundle(strengthMonthDays, [9], { min: 4, max: 10, showOnChart: true }),
            weeks: bundle(strengthMonthWeeks, [9], { min: 4, max: 10, showOnChart: true }),
        },
        year: {
            weeks: bundle(strengthYearWeeks, [8], { min: 3, max: 10, showOnChart: true }),
            months: bundle(strengthYearMonths, [8], { min: 3, max: 10, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(strengthTodayHours, [9], { min: 5, max: 10, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(strengthWeekHours, [9], { min: 5, max: 10, showOnChart: true }),
            days: bundle(strengthWeekDays, [9], { min: 5, max: 10, showOnChart: true }),
        },
        custom_8_46_days: {
            days: bundle(strengthMonthDays, [9], { min: 4, max: 10, showOnChart: true }),
            weeks: bundle(strengthMonthWeeks, [9], { min: 4, max: 10, showOnChart: true }),
        },
        custom_47_plus: {
            weeks: bundle(strengthYearWeeks, [8], { min: 3, max: 10, showOnChart: true }),
            months: bundle(strengthYearMonths, [8], { min: 3, max: 10, showOnChart: true }),
        },
    },
    brood: {
        today: { hours: bundle(broodTodayHours, [5], { min: 1, max: 7, showOnChart: true }) },
        yesterday: { hours: bundle(broodYesterdayHours, [5], { min: 1, max: 7, showOnChart: true }) },
        week: {
            hours: bundle(broodWeekHours, [5], { min: 1, max: 7, showOnChart: true }),
            days: bundle(broodWeekDays, [5], { min: 1, max: 7, showOnChart: true }),
        },
        month: {
            days: bundle(broodMonthDays, [5], { min: 0, max: 8, showOnChart: true }),
            weeks: bundle(broodMonthWeeks, [5], { min: 0, max: 8, showOnChart: true }),
        },
        year: {
            weeks: bundle(broodYearWeeks, [4], { min: 0, max: 8, showOnChart: true }),
            months: bundle(broodYearMonths, [4], { min: 0, max: 8, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(broodTodayHours, [5], { min: 1, max: 7, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(broodWeekHours, [5], { min: 1, max: 7, showOnChart: true }),
            days: bundle(broodWeekDays, [5], { min: 1, max: 7, showOnChart: true }),
        },
        custom_8_46_days: {
            days: bundle(broodMonthDays, [5], { min: 0, max: 8, showOnChart: true }),
            weeks: bundle(broodMonthWeeks, [5], { min: 0, max: 8, showOnChart: true }),
        },
        custom_47_plus: {
            weeks: bundle(broodYearWeeks, [4], { min: 0, max: 8, showOnChart: true }),
            months: bundle(broodYearMonths, [4], { min: 0, max: 8, showOnChart: true }),
        },
    },
    sound: {
        today: { hours: bundle(soundTodayHours, [60], { min: 35, max: 75, showOnChart: true }) },
        yesterday: { hours: bundle(soundYesterdayHours, [60], { min: 35, max: 75, showOnChart: true }) },
        week: {
            hours: bundle(soundWeekHours, [60], { min: 35, max: 80, showOnChart: true }),
            days: bundle(soundWeekDays, [60], { min: 35, max: 80, showOnChart: true }),
        },
        month: {
            days: bundle(soundMonthDays, [60], { min: 30, max: 85, showOnChart: true }),
            weeks: bundle(soundMonthWeeks, [60], { min: 30, max: 85, showOnChart: true }),
        },
        year: {
            weeks: bundle(soundYearWeeks, [58], { min: 30, max: 85, showOnChart: true }),
            months: bundle(soundYearMonths, [58], { min: 30, max: 85, showOnChart: true }),
        },
        custom_1_2_days: { hours: bundle(soundTodayHours, [60], { min: 35, max: 75, showOnChart: true }) },
        custom_3_7_days: {
            hours: bundle(soundWeekHours, [60], { min: 35, max: 80, showOnChart: true }),
            days: bundle(soundWeekDays, [60], { min: 35, max: 80, showOnChart: true }),
        },
        custom_8_46_days: {
            days: bundle(soundMonthDays, [60], { min: 30, max: 85, showOnChart: true }),
            weeks: bundle(soundMonthWeeks, [60], { min: 30, max: 85, showOnChart: true }),
        },
        custom_47_plus: {
            weeks: bundle(soundYearWeeks, [58], { min: 30, max: 85, showOnChart: true }),
            months: bundle(soundYearMonths, [58], { min: 30, max: 85, showOnChart: true }),
        },
    },
};
